import { WindowLocationService } from '~commonInfra/services/WindowLocationService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';

import { Telemetry, ValidTelemetryEvent } from '../services/Telemetry';
import {
  RedirectSourceEvent,
  TelemetryEvents,
  VizPortalNavigationEvent
} from '../telemetryEvents/TelemetryEvents';
import { Helpers } from '../utils/Helpers';

export interface StateTransition {
  newStateName: string;
  oldStateName: string;
  contentId: string;
}

export namespace UrlTelemetryService {
  export function sendUrlEventsOnStateChange(stateTransition: StateTransition): void {
    const search: any = WindowLocationService.getHashQueryObject();
    let redirectSourceUrlParam = search[UrlParamKeys.RedirectSource];
    if (redirectSourceUrlParam) {
      sendRedirectSourceEventAndRemoveUrlParam(redirectSourceUrlParam);
    }
    sendNavigationEvent(stateTransition);
  }

  /**
   * Sends a telemetry event if the RedirectSource URL param exists.
   * This assumes that the SSO login carries the query params over through login.
   */
  function sendRedirectSourceEventAndRemoveUrlParam(source: string) {
    // Assuming that source has already been cleaned by the redirect handler
    Telemetry.sendEvent<RedirectSourceEvent>({
      type: TelemetryEvents.RedirectSource,
      source: source,
    });
    WindowLocationService.deleteQueryParameter(UrlParamKeys.RedirectSource);
  }

  function sendNavigationEvent(stateTransition: StateTransition) {
    if (stateTransition.newStateName !== stateTransition.oldStateName) {
      Telemetry.sendEvent<VizPortalNavigationEvent>({
        type: TelemetryEvents.VizPortalNavigation,
        newState: stateTransition.newStateName,
        oldState: stateTransition.oldStateName,
        contentId: stateTransition.contentId,
        mixedContentEnabled: true // TODO: We should figure out some way to remove mixedContentEnabled from the telemetry
      });
    }
  }

  /**
   * Send telemetry if a data quality subscription event occurred. This event
   * occurs when a link from the data quality portion of a subscription email is
   * followed. Special query string parameters are added to URLs in subscription
   * emails and removed here if present.
   */
  export function maybeSendDataQualitySubscriptionEvent(contentId: string, contentType: string, queryParams?: any) {
    queryParams = queryParams || WindowLocationService.getHashQueryObject();
    const hasParam = Object.hasOwnProperty.bind(queryParams);
    if (hasParam(UrlParamKeys.FromSubscriptionEmail) && hasParam(UrlParamKeys.IsSevere)) {
      Telemetry.sendEvent<DataQualitySubscriptionEvent>({
        type: 'data_quality_subscription',
        is_severe: Helpers.BooleanQueryParamRegex.test(queryParams[UrlParamKeys.IsSevere]),
        content_id: contentId,
        content_type: contentType
      });
      WindowLocationService.deleteQueryParameter(UrlParamKeys.FromSubscriptionEmail);
      WindowLocationService.deleteQueryParameter(UrlParamKeys.IsSevere);
    }
  }

  // user_id and site_id are added to payload by Telemetry.sendEvent
  interface DataQualitySubscriptionEvent extends ValidTelemetryEvent {
    type: 'data_quality_subscription';
    is_severe: boolean;
    content_id: string;
    content_type: string;
  }
}

export type UrlTelemetryService = typeof UrlTelemetryService;
